import { useCallback, useEffect, useRef, useState } from 'react';
import { notificationCenterApi } from '../../api/notificationCenterApi';
import { useAuth } from '../../auth/AuthContext';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { subscribeSocialNotificationUnread } from '../social-notification/socialNotificationUnreadBus';
import { subscribeGroupTripNoticeUnread } from '../group-trip-notice/groupTripNoticeUnreadBus';
import {
  badgeFromNotificationCenterUnread,
  normalizeNotificationCenterUnreadCount,
  notificationCenterBadgeAriaLabel,
} from './notificationCenterModel';

/**
 * Notification Center combined unread（canonical Center API）。
 * 訂閱 Social / GT unread bus 僅觸發 refetch；不在 client 做 Social+GT sum。
 */
export function useNotificationCenterUnreadCount() {
  const { status } = useAuth();
  const { t } = useTravelTranslation();
  const generation = useRef(0);
  const [count, setCount] = useState(null);
  const [badge, setBadge] = useState(null);

  const refresh = useCallback(async () => {
    if (status !== 'authenticated') {
      setCount(null);
      setBadge(null);
      return null;
    }
    const requestId = ++generation.current;
    try {
      const payload = await notificationCenterApi.getUnreadCount();
      if (requestId !== generation.current) return null;
      const next = normalizeNotificationCenterUnreadCount(payload);
      setCount(next);
      setBadge(badgeFromNotificationCenterUnread(next));
      return next;
    } catch {
      if (requestId !== generation.current) return null;
      // refresh 失敗：保留既有成功結果，不假清為 0；首次失敗維持 null。
      return null;
    }
  }, [status]);

  useEffect(() => {
    refresh();
    return () => { generation.current += 1; };
  }, [refresh]);

  useEffect(() => {
    const unsubSocial = subscribeSocialNotificationUnread(() => { refresh(); });
    const unsubGt = subscribeGroupTripNoticeUnread(() => { refresh(); });
    return () => {
      unsubSocial();
      unsubGt();
    };
  }, [refresh]);

  const ariaLabel = notificationCenterBadgeAriaLabel(badge, t);

  return { count, badge, refresh, ariaLabel };
}
